/**
 * @file useToast.js
 * @description Toast notification system with context provider and hook.
 *
 * Usage:
 *   // Wrap your app:
 *   <ToastProvider><App /></ToastProvider>
 *
 *   // In any component:
 *   const toast = useToast();
 *   toast.success("Inspection saved!");
 *   toast.error("Upload failed");
 *   toast.warning("Low storage");
 *   toast.info("New version available", { action: { label: "Update", onClick: () => {} } });
 */
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

const MAX_VISIBLE = 3;
let toastCounter = 0;

/**
 * @typedef {Object} ToastOptions
 * @property {number} [duration=5000] - Auto-dismiss time in ms (0 to disable)
 * @property {{ label: string, onClick: Function }} [action] - Action button
 * @property {boolean} [dismissible=true] - Show X button
 */

/**
 * Provider component that manages toast state and renders the toast container.
 */
export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    // Mark as exiting for animation
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
      if (timersRef.current[id]) {
        clearTimeout(timersRef.current[id]);
        delete timersRef.current[id];
      }
    }, 300);
  }, []);

  const addToast = useCallback((type, message, options = {}) => {
    const id = ++toastCounter;
    const duration = options.duration !== undefined ? options.duration : 5000;

    const toast = {
      id,
      type,
      message,
      duration,
      action: options.action || null,
      dismissible: options.dismissible !== false,
      createdAt: Date.now(),
      exiting: false,
    };

    setToasts(prev => {
      const updated = [...prev, toast];
      // If exceeding max, mark oldest for removal
      if (updated.filter(t => !t.exiting).length > MAX_VISIBLE) {
        const oldest = updated.find(t => !t.exiting);
        if (oldest) {
          setTimeout(() => removeToast(oldest.id), 0);
        }
      }
      return updated;
    });

    // Auto-dismiss
    if (duration > 0) {
      timersRef.current[id] = setTimeout(() => removeToast(id), duration);
    }

    return id;
  }, [removeToast]);

  const api = React.useMemo(() => ({
    success: (msg, opts) => addToast('success', msg, opts),
    error: (msg, opts) => addToast('error', msg, { duration: 8000, ...opts }),
    warning: (msg, opts) => addToast('warning', msg, opts),
    info: (msg, opts) => addToast('info', msg, opts),
    dismiss: removeToast,
    dismissAll: () => setToasts([]),
  }), [addToast, removeToast]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}

/**
 * Hook to access toast methods.
 * @returns {{ success: Function, error: Function, warning: Function, info: Function, dismiss: Function, dismissAll: Function }}
 */
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}

export default useToast;

/* ---- Toast Container (renders the stack) ---- */

const typeConfig = {
  success: { color: 'var(--color-success-600)', bg: 'var(--color-success-50)', icon: 'check' },
  error:   { color: 'var(--color-error-600)',   bg: 'var(--color-error-50)',   icon: 'x' },
  warning: { color: 'var(--color-warning-600)', bg: 'var(--color-warning-50)', icon: 'alert' },
  info:    { color: 'var(--color-info-600)',     bg: 'var(--color-info-50)',    icon: 'info' },
};

function ToastIcon({ type }) {
  const stroke = 'currentColor';
  switch (type) {
    case 'success':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
      );
    case 'error':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      );
    case 'warning':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    default:
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2.5" strokeLinecap="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      );
  }
}

/* Inject keyframes */
let toastAnimInjected = false;
function injectToastAnimations() {
  if (toastAnimInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.textContent = `
@keyframes ib-toast-in {
  from { opacity: 0; transform: translateX(100%) scale(0.95); }
  to { opacity: 1; transform: translateX(0) scale(1); }
}
@keyframes ib-toast-in-mobile {
  from { opacity: 0; transform: translateY(-100%) scale(0.95); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes ib-toast-out {
  from { opacity: 1; transform: translateX(0); }
  to { opacity: 0; transform: translateX(100%); }
}
@keyframes ib-toast-progress {
  from { width: 100%; }
  to { width: 0%; }
}`;
  document.head.appendChild(style);
  toastAnimInjected = true;
}

function ToastContainer({ toasts, onDismiss }) {
  React.useEffect(() => { injectToastAnimations(); }, []);

  if (toasts.length === 0) return null;

  // Detect mobile for positioning
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: 'fixed',
        ...(isMobile
          ? { top: 'var(--space-4)', left: 'var(--space-4)', right: 'var(--space-4)' }
          : { bottom: 'var(--space-4)', right: 'var(--space-4)', width: '380px' }
        ),
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'column-reverse',
        gap: 'var(--space-2)',
        zIndex: 1400,
        pointerEvents: 'none',
      }}
    >
      {toasts.map((toast) => {
        const config = typeConfig[toast.type] || typeConfig.info;
        const animName = toast.exiting
          ? 'ib-toast-out'
          : (isMobile ? 'ib-toast-in-mobile' : 'ib-toast-in');

        return (
          <div
            key={toast.id}
            role="alert"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-4)',
              backgroundColor: 'var(--surface-raised)',
              border: `1px solid ${config.color}20`,
              borderLeft: `4px solid ${config.color}`,
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-lg)',
              pointerEvents: 'auto',
              overflow: 'hidden',
              position: 'relative',
              animation: `${animName} 0.3s var(--ease-out) forwards`,
            }}
          >
            {/* Icon */}
            <span style={{
              color: config.color,
              flexShrink: 0,
              marginTop: '2px',
            }}>
              <ToastIcon type={toast.type} />
            </span>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{
                margin: 0,
                fontSize: 'var(--text-body-sm)',
                fontWeight: 'var(--weight-medium)',
                color: 'var(--text-primary)',
                lineHeight: 'var(--leading-normal)',
              }}>
                {toast.message}
              </p>

              {toast.action && (
                <button
                  type="button"
                  onClick={() => {
                    toast.action.onClick();
                    onDismiss(toast.id);
                  }}
                  style={{
                    marginTop: 'var(--space-1_5)',
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    color: config.color,
                    fontSize: 'var(--text-body-sm)',
                    fontWeight: 'var(--weight-semibold)',
                    fontFamily: 'var(--font-family)',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  {toast.action.label}
                </button>
              )}
            </div>

            {/* Dismiss button */}
            {toast.dismissible && (
              <button
                type="button"
                onClick={() => onDismiss(toast.id)}
                aria-label="Dismiss notification"
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: 'none',
                  color: 'var(--text-tertiary)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  padding: 0,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}

            {/* Progress bar */}
            {toast.duration > 0 && !toast.exiting && (
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                height: '3px',
                backgroundColor: config.color,
                opacity: 0.3,
                borderRadius: '0 0 0 var(--radius-lg)',
                animation: `ib-toast-progress ${toast.duration}ms linear forwards`,
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}
